import React, { useEffect, useRef, useState } from 'react';
import { BaselineSeries, ColorType, CrosshairMode, LineSeries, LineStyle, createChart } from 'lightweight-charts';
import { useTheme } from '../../../contexts/ThemeContext';
import { gainFromIndex } from '../rsSeries';
import RsHelpTip from './RsHelpTip';

const AVG_COLOR = '#38bdf8';
const UNIVERSE_COLOR = '#34d399';
const BENCH_COLOR = '#94a3b8';
const STRONG_COLOR = '#07CBA3';
const WEAK_COLOR = '#F73263';
const RIGHT_PAD = 12;

const TIME_SCALE = {
  borderVisible: false,
  timeVisible: true,
  secondsVisible: false,
  rightOffset: RIGHT_PAD,
  lockVisibleTimeRangeOnResize: true,
  shiftVisibleRangeOnNewBar: false,
};

const BASKET_HINT = 'The top half is the equal-weight price of these tickers from the first bar, as percent change, next to the benchmark. It is not the profit from buying or selling the relative-strength signals. The bottom half is average relative strength from 0 to 100. Green fill is strength above 50; red fill is weakness below 50.';

export const RS_TICKER_LINE_COLORS = ['#38bdf8', '#a78bfa', '#fbbf24', '#34d399', '#fb7185', '#f97316', '#e879f9', '#94a3b8'];

const percentFormat = {
  type: 'custom',
  minMove: 0.1,
  formatter: (price) => formatGain(price),
};

const scoreFormat = {
  type: 'custom',
  minMove: 1,
  formatter: (price) => (Number.isFinite(price) ? price.toFixed(0) : ''),
};

function formatGain(value) {
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function formatScore(value) {
  if (!Number.isFinite(value)) return '—';
  return value.toFixed(1);
}

function withAlpha(hex, alpha) {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return `rgba(56, 189, 248, ${alpha})`;
  const r = Number.parseInt(raw.slice(0, 2), 16);
  const g = Number.parseInt(raw.slice(2, 4), 16);
  const b = Number.parseInt(raw.slice(4, 6), 16);
  if (![r, g, b].every(Number.isFinite)) return `rgba(56, 189, 248, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function rsFillColor(value, colors) {
  if (!Number.isFinite(value) || value === 50) return AVG_COLOR;
  return value > 50 ? (colors.success || STRONG_COLOR) : (colors.danger || WEAK_COLOR);
}

function linePoints(points) {
  return gainFromIndex(points).map((point) => ({ time: point.timeSec, value: point.gain }));
}

function lastGain(points) {
  const rows = gainFromIndex(points);
  return rows.length ? rows[rows.length - 1].gain : null;
}

function tickerSeriesSignature(tickerSeries) {
  return (tickerSeries || []).map((row) => {
    const last = row.points?.[row.points.length - 1];
    return `${row.ticker}:${row.points?.length || 0}:${last?.score || 0}:${row.color || ''}`;
  }).join('|');
}

export default function RsGroupChart({
  averageRs = [],
  groupIndex = [],
  benchmarkIndex = [],
  tickerSeries = [],
  highlightedTicker = null,
  benchmark = 'SPY',
  threshold = 60,
  height = 440,
  hint = BASKET_HINT,
  testId = 'rs-study-group-chart',
  emptyTestId = 'rs-study-group-chart-empty',
}) {
  const { colors } = useTheme();
  const containerRef = useRef(null);
  const viewRef = useRef({ range: null, dataLength: null });
  const [hover, setHover] = useState(null);
  const split = groupIndex.length > 0 || benchmarkIndex.length > 0;
  const visibleTickers = (tickerSeries || []).filter((row) => (row.points || []).length > 0);
  const dataRef = useRef({});
  dataRef.current = {
    averageRs,
    groupIndex,
    benchmarkIndex,
    tickerSeries: visibleTickers,
    highlightedTicker,
    split,
    benchmark,
    threshold,
    height,
    colors,
  };
  const signature = [
    averageRs.length,
    averageRs[averageRs.length - 1]?.timeSec,
    averageRs[averageRs.length - 1]?.score,
    groupIndex.length,
    groupIndex[groupIndex.length - 1]?.value,
    benchmarkIndex.length,
    benchmarkIndex[benchmarkIndex.length - 1]?.value,
    tickerSeriesSignature(visibleTickers),
    highlightedTicker || '',
    benchmark,
    threshold,
    split ? 'split' : 'rs',
  ].join(':');

  useEffect(() => {
    const el = containerRef.current;
    const data = dataRef.current;
    if (!el || (!data.averageRs.length && !data.tickerSeries.length)) return undefined;
    const chart = createChart(el, {
      width: el.clientWidth || 640,
      height,
      layout: {
        background: { type: ColorType.Solid, color: colors.bgPrimary || '#0C0F19' },
        textColor: colors.textMuted || '#7B8A9E',
        fontSize: 11,
        panes: {
          separatorColor: colors.border || '#1E2634',
          separatorHoverColor: colors.border || '#1E2634',
        },
      },
      grid: {
        vertLines: { color: colors.border || '#1E2634' },
        horzLines: { color: colors.border || '#1E2634' },
      },
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: true, borderVisible: false, entireTextOnly: true },
      timeScale: TIME_SCALE,
      crosshair: { mode: CrosshairMode.Normal },
    });

    const includeZero = (original) => {
      const info = original?.();
      if (!info?.priceRange) return { priceRange: { minValue: -1, maxValue: 1 } };
      return {
        priceRange: {
          minValue: Math.min(0, info.priceRange.minValue),
          maxValue: Math.max(0, info.priceRange.maxValue),
        },
      };
    };
    const lockScore = () => ({ priceRange: { minValue: 0, maxValue: 100 } });

    let basketSeries = null;
    let benchSeries = null;
    const rsPane = split ? 1 : 0;
    if (split) {
      if (groupIndex.length) {
        basketSeries = chart.addSeries(LineSeries, {
          color: UNIVERSE_COLOR,
          lineWidth: 2,
          priceScaleId: 'right',
          priceFormat: percentFormat,
          title: 'Basket',
          priceLineVisible: false,
          autoscaleInfoProvider: includeZero,
        }, 0);
        basketSeries.setData(linePoints(groupIndex));
      }
      if (benchmarkIndex.length) {
        benchSeries = chart.addSeries(LineSeries, {
          color: BENCH_COLOR,
          lineWidth: 1,
          priceScaleId: 'right',
          priceFormat: percentFormat,
          title: benchmark,
          priceLineVisible: false,
          autoscaleInfoProvider: includeZero,
        }, 0);
        benchSeries.setData(linePoints(benchmarkIndex));
      }
      (basketSeries || benchSeries)?.createPriceLine({
        price: 0,
        color: colors.textMuted || '#7B8A9E',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        title: '0%',
      });
      chart.addPane();
      chart.panes()[0].setStretchFactor(1);
      chart.panes()[1].setStretchFactor(1);
      chart.priceScale('left', 0).applyOptions({ visible: false });
      chart.priceScale('left', 1).applyOptions({ visible: false });
      chart.priceScale('right', 0).applyOptions({ borderVisible: false, scaleMargins: { top: 0.16, bottom: 0.08 } });
      chart.priceScale('right', 1).applyOptions({ borderVisible: false, scaleMargins: { top: 0.16, bottom: 0.08 } });
    }

    const strong = colors.success || STRONG_COLOR;
    const weak = colors.danger || WEAK_COLOR;
    const rsSeries = data.averageRs.length ? chart.addSeries(BaselineSeries, {
      baseValue: { type: 'price', price: 50 },
      topLineColor: strong,
      topFillColor1: withAlpha(strong, 0.38),
      topFillColor2: withAlpha(strong, 0.04),
      bottomLineColor: weak,
      bottomFillColor1: withAlpha(weak, 0.04),
      bottomFillColor2: withAlpha(weak, 0.38),
      lineWidth: 2,
      priceScaleId: 'right',
      priceFormat: scoreFormat,
      title: 'Avg',
      priceLineVisible: false,
      autoscaleInfoProvider: lockScore,
    }, rsPane) : null;
    if (rsSeries) {
      rsSeries.setData(data.averageRs
        .filter((point) => Number.isFinite(point?.timeSec) && Number.isFinite(point?.score))
        .map((point) => ({ time: point.timeSec, value: point.score })));
      if (Number.isFinite(data.threshold)) {
        rsSeries.createPriceLine({
          price: data.threshold,
          color: colors.primary || '#818CF8',
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          title: String(data.threshold),
        });
      }
      rsSeries.createPriceLine({
        price: 50,
        color: colors.textMuted || '#7B8A9E',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        title: '50',
      });
    }

    const tickerApis = [];
    data.tickerSeries.forEach((row, index) => {
      const highlighted = row.ticker === data.highlightedTicker;
      const series = chart.addSeries(LineSeries, {
        color: row.color || RS_TICKER_LINE_COLORS[index % RS_TICKER_LINE_COLORS.length],
        lineWidth: highlighted ? 3 : 1.5,
        priceScaleId: 'right',
        priceFormat: scoreFormat,
        title: row.ticker,
        priceLineVisible: false,
        lastValueVisible: true,
        autoscaleInfoProvider: lockScore,
      }, rsPane);
      series.setData((row.points || [])
        .filter((point) => Number.isFinite(point?.timeSec) && Number.isFinite(point?.score))
        .map((point) => ({ time: point.timeSec, value: point.score })));
      if (!rsSeries && index === 0) {
        series.createPriceLine({
          price: 50,
          color: colors.textMuted || '#7B8A9E',
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          title: '50',
        });
      }
      tickerApis.push({ ticker: row.ticker, series });
    });

    const timeScale = chart.timeScale();
    const pointCount = data.averageRs.length || data.tickerSeries[0]?.points?.length || 0;
    if (viewRef.current.dataLength !== pointCount) {
      viewRef.current = { range: null, dataLength: pointCount };
    }
    if (viewRef.current.range) {
      timeScale.setVisibleLogicalRange(viewRef.current.range);
    } else {
      timeScale.fitContent();
      timeScale.applyOptions({ rightOffset: RIGHT_PAD });
    }
    timeScale.subscribeVisibleLogicalRangeChange((range) => {
      if (range) viewRef.current.range = range;
    });
    const onMove = (param) => {
      if (!param?.time || !param.seriesData) {
        setHover(null);
        return;
      }
      const read = (series) => {
        if (!series) return null;
        const row = param.seriesData.get(series);
        return row && Number.isFinite(row.value) ? row.value : null;
      };
      const tickers = {};
      tickerApis.forEach(({ ticker, series }) => {
        tickers[ticker] = read(series);
      });
      setHover({
        basket: read(basketSeries),
        bench: read(benchSeries),
        rs: read(rsSeries),
        tickers,
      });
    };
    chart.subscribeCrosshairMove(onMove);
    let observer;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(() => {
        chart.applyOptions({ width: el.clientWidth || 640, height });
      });
      observer.observe(el);
    }
    return () => {
      observer?.disconnect();
      chart.remove();
    };
  }, [signature, height, colors.bgPrimary, colors.border, colors.primary, colors.success, colors.danger, colors.textMuted, benchmark, threshold, split]);

  if (!averageRs.length && !visibleTickers.length) {
    return (
      <div data-testid={emptyTestId} style={{ fontSize: 13, color: colors.textMuted }}>
        No group relative strength yet.
      </div>
    );
  }

  const shown = {
    basket: hover?.basket ?? lastGain(groupIndex),
    bench: hover?.bench ?? lastGain(benchmarkIndex),
    rs: hover?.rs ?? averageRs[averageRs.length - 1]?.score,
  };
  const legend = [
    split && groupIndex.length ? { color: UNIVERSE_COLOR, label: 'Basket % gain', value: formatGain(shown.basket) } : null,
    split && benchmarkIndex.length ? { color: BENCH_COLOR, label: `${benchmark} % gain`, value: formatGain(shown.bench) } : null,
    averageRs.length ? { color: rsFillColor(shown.rs, colors), label: 'Average relative strength', value: formatScore(shown.rs) } : null,
  ].filter(Boolean);

  return (
    <div data-testid={testId}>
      <div style={{ position: 'relative' }}>
        <div ref={containerRef} style={{ width: '100%', height }} />
        {split ? (
          <>
            <div
              style={{
                position: 'absolute',
                top: 8,
                left: 10,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                pointerEvents: 'none',
              }}
            >
              <span style={{ fontSize: 11, color: colors.textLight || colors.textMuted }}>
                Basket valuation, % from start
              </span>
              <span style={{ pointerEvents: 'auto' }}>
                <RsHelpTip id="rs-basket-hint" text={hint} />
              </span>
            </div>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                marginTop: 8,
                left: 10,
                zIndex: 2,
                pointerEvents: 'none',
                fontSize: 11,
                color: colors.textLight || colors.textMuted,
              }}
            >
              Average relative strength
            </div>
          </>
        ) : (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 10,
              zIndex: 2,
              fontSize: 11,
              color: colors.textLight || colors.textMuted,
              pointerEvents: 'none',
            }}
          >
            Average relative strength
          </div>
        )}
      </div>
      <div
        data-testid="rs-study-group-legend"
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginTop: 10,
        }}
      >
        {legend.map((item) => (
          <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: item.color, flex: '0 0 auto' }} />
            {item.label}
            <span style={{ color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>{item.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
